import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppSelector } from 'redux-store/hooks'
import {
  channelThemeIsLoadingSelector,
  channelThemeSelector
} from 'redux-store/channelTheme/channelTheme.selectors'
import { channelSelector } from 'redux-store/channel/channel.selectors'
import {
  loadStateFromStorage,
  saveStateToStorage
} from 'helpers/localStorageHelper'
import { RateLimitContext } from '../types'
import { RATE_LIMIT_PAGE_PATH, RATE_LIMIT_STORAGE_KEY } from '../consts'

export const useRateLimitContext = () => {
  const location = useLocation()
  const history = useHistory()

  const channelThemeData = useAppSelector(channelThemeSelector)
  const channelThemeIsLoading = useAppSelector(channelThemeIsLoadingSelector)
  const { data, isLoading, isError } = useAppSelector(channelSelector)
  const companySlug = data?.subdomain ?? null

  const [state, setState] = useState<RateLimitContext | null>(
    () => loadStateFromStorage(RATE_LIMIT_STORAGE_KEY) || null
  )

  const isChannelDataReady = !isLoading && !isError && data && companySlug
  const isChannelThemeReady = !channelThemeIsLoading && channelThemeData
  const shouldUpdateTheState = isChannelDataReady && isChannelThemeReady

  useEffect(() => {
    if (location.pathname === RATE_LIMIT_PAGE_PATH) return

    if (shouldUpdateTheState) {
      const updatedContext = {
        companySlug,
        colors: { ...channelThemeData },
        returnUrl: window.location.href
      }
      saveStateToStorage(RATE_LIMIT_STORAGE_KEY, updatedContext)
      setState(updatedContext)
    }
  }, [shouldUpdateTheState, companySlug, channelThemeData, location.pathname])

  const goHome = useCallback(() => {
    if (state?.companySlug) {
      window.location.replace(`/${state.companySlug}`)
    }
  }, [state?.companySlug])

  const goBack = useCallback(() => {
    if (state?.returnUrl) {
      window.location.replace(state.returnUrl)
      return
    }
    history.goBack()
  }, [state?.returnUrl, history])

  const results = useMemo(
    () => ({
      state,
      actions: { goHome, goBack }
    }),
    [state, goHome, goBack]
  )

  return results
}
